import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { getCurrencySybol } from 'sdk/utils/currency';
import {
  formatAssetStat, formatDescription, getItemPostffix, getItemSuffix,
} from 'sdk/utils/formatValues';
import Button from 'components/shared/Button';
import Chart from 'components/shared/Chart';
import CloseBtn from 'components/shared/CloseBtn';
import { ReactComponent as IconShare } from 'images/share.svg';
import ReportModal from 'components/shared/modals/ReportModal';
import Skeleton from 'components/shared/Skeleton';
import useAssetFollowers from 'sdk/hooks/follow/useAssetFollowers';
import useFollowAsset from 'sdk/hooks/follow/useFollowAsset';
import useUser from 'sdk/hooks/user/useProfile';
import { useAssetChartData } from 'sdk/hooks/assets/useAssetChartData';
import SignUp from 'modules/auth/components/SignUp';
import { useActions } from 'store/useActions';
import ShareModal from '../ShareModal';
import styles from './AssetDetails.module.scss';

function AssetDetails({
  className,
  activeAsset,
  setActiveAsset,
  onSaveCurrentState,
}: {
  className?: string,
  activeAsset: string,
  setActiveAsset: (v: any) => void,
  onSaveCurrentState?: () => void,
}) {
  const navigate = useNavigate()
  const [showFullDescription, setShowFullDescription] = useState(false)

  const {
    isPressIn,
    setPressIn,
    lastPrice,
    period,
    setPeriod,
    selectedPrice,
    selectPriceHandler,
    assetData,
    periods,
    oldPrice,
    setIsPreMarket,
    isAfterHours,
    setIsAfterHours,
    delta,
    afterHoursDelta,
    showDays,
    isYear,
    showTime,
    assetEventData,
    assetEventsTotalLength,
    getTopText,
    getBottomText,
    isETF,
    isCurrency,
    isStock,
    isFall,
    isOpenMarket,
  } = useAssetChartData(activeAsset)

  const [showViewMore, setViewMore] = useState(false)
  const [isShowSharelModal, setIsShowSharelModal] = useState(false)
  const [isReportModal, setIsReportModal] = useState(false)
  const [isFollowedByMe, setIsFollowedByMe] = useState(false)
  const { data: currentUser } = useUser()
  const [isSignUpVisible, setSignUpVisible] = useState<boolean>(false)
  const { setIsGetAppModal } = useActions()

  const statsFinance: any = {}
  statsFinance['low today'] = assetData?.quote?.low
  statsFinance['high today'] = assetData?.quote?.high
  statsFinance['52 Week low'] = assetData?.quote?.fifty_two_week.low
  statsFinance['52 Week high'] = assetData?.quote?.fifty_two_week.high
  statsFinance.open = assetData?.quote?.open
  if (assetData?.quote?.is_market_open) {
    statsFinance.close = assetData?.quote?.close
  }

  statsFinance.volume = assetData?.quote?.volume
  statsFinance['Average volume'] = assetData?.quote?.average_volume
  statsFinance.Exchange = assetData?.quote?.exchange

  if (isStock && assetData?.stats_finance?.['Stock Outstanding']) {
  // eslint-disable-next-line no-unsafe-optional-chaining, operator-assignment
    statsFinance['Market cap'] = assetData.stats_finance['Stock Outstanding'] * assetData.latest_price.price
  }
  if ((isStock || isETF) && typeof assetData?.stats_finance?.['Diluted EPS TTM'] === 'number') {
    // eslint-disable-next-line no-unsafe-optional-chaining
    statsFinance['Price-Earnings Ratio'] = assetData?.latest_price.price / assetData?.stats_finance?.['Diluted EPS TTM']
  }
  if (isStock) {
    // eslint-disable-next-line no-unsafe-optional-chaining
    statsFinance['Dividient yield'] = assetData?.stats_finance?.['Dividend yield'] * 100
    statsFinance.Beta = assetData?.stats_finance?.Beta
  }
  // statsFinance

  const assetFollowersData = useAssetFollowers(activeAsset)

  useEffect(() => {
    setIsFollowedByMe(Boolean(assetFollowersData?.data?.isFollowedByMe))
  }, [assetFollowersData?.data?.isFollowedByMe])

  const followingMutation = useFollowAsset(activeAsset)

  const descriptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setViewMore(assetData?.description?.length > 166);
  }, [assetData?.description])

  if (!assetData) {
    return (
      <div className={className} />
    )
  }

  return (
    <div className={clsx(
      className,
      styles.container,
      (isFall && assetEventData.data && !assetEventData.isLoading) && styles.container_fall,
      (!isFall && assetEventData.data && !assetEventData.isLoading) && styles.container_up,
    )}
    >
      {isSignUpVisible ? (
        <SignUp
          onClose={() => {
            setSignUpVisible(false)
          }}
          goToSignIn={() => {
            setSignUpVisible(false)
          }}
        />
      ) : null}
      <div
        className={clsx(styles.subContainer, 'scroll scroll-y', 'swiper-no-swiping')}
        onWheel={(e: any) => {
          e.stopPropagation()
          e.preventDefault()
          e.nativeEvent.stopPropagation()
          e.nativeEvent.stopImmediatePropagation()
        }}
      >
        <CloseBtn
          className={styles.btnClose}
          onClick={() => setActiveAsset(null)}
        />
        <IconShare className={styles.btnShare} onClick={() => setIsShowSharelModal(true)} />

        <ReportModal
          id="report"
          type="asset"
          isOpen={isReportModal}
          onClose={() => setIsReportModal(false)}
          postId={' '}
        />
        <ShareModal
          isOpen={isShowSharelModal}
          onClose={() => setIsShowSharelModal(false)}
          handleOpenReportModal={() => setIsReportModal(true)}
        />

        <div className={styles.catIcon}>
          {assetData?.icon ? (<img src={assetData?.icon} alt={assetData?.name} />) : null}
        </div>

        <div className={styles.name}>
          {assetData?.name}
        </div>

        <div className={styles.value}>
          {
          (!lastPrice && lastPrice !== 0) ? (
            <Skeleton
              className={styles.btnShare}
              style={{ width: '40px' }}
            />
          )
            : `${getCurrencySybol(assetData?.currency_name)} ${formatAssetStat(isPressIn ? selectedPrice?.price : lastPrice)}`
        }

        </div>
        <div className={styles.percentContainer}>
          {!(isPressIn && isAfterHours) ? (
            <div className={clsx(styles.percent, (delta < 0) && styles.percentRed)}>
              {getTopText()}
            </div>
          ) : null}
          <div className={clsx(
            styles.percent,
            styles.afterHoursPercent,
            (((isOpenMarket || isPressIn)
              ? afterHoursDelta : assetData?.quote?.extended_change) < 0) && styles.percentRed,
          )}
          >
            {getBottomText()}
          </div>
        </div>

        <Chart
          className={styles.chart}
          data={assetEventData.data || []}
          showDays={showDays}
          showTime={showTime}
          isYear={isYear}
          curValue={period !== '24H' ? oldPrice : assetData?.quote?.previous_close}
          setMouseIn={setPressIn}
          onSelect={selectPriceHandler}
          setIsPremarket={setIsPreMarket}
          setIsAfterHours={setIsAfterHours}
          fall={isFall}
          isCurrency={isCurrency}
          isShortAsset={assetData?.is_short_asset}
          chartDotsCount={assetEventsTotalLength}
        />

        <div className={styles.graphFilters}>
          {periods.map((item: string) => (
            <div
              key={item}
              className={clsx(styles.graphFilter, (period === item) && styles.graphFilterActive)}
              onClick={() => setPeriod(item)}
            >
              {item}
            </div>
          ))}
        </div>

        <div
          className={clsx(styles.infoContainer)}
          onWheel={(e: any) => {
            e.stopPropagation()
            e.preventDefault()
            e.nativeEvent.stopPropagation()
            e.nativeEvent.stopImmediatePropagation()
          }}
        >

          <div className={styles.infoTitle}>
            {`About ${assetData?.name}`}
            {' '}
            {
            !isFollowedByMe
              ? (
                <Button
                  className={styles.btnFollow}
                  onClick={() => {
                    if (window.innerWidth < 768) {
                      setIsGetAppModal(Boolean(window.innerWidth < 768))
                      return
                    }
                    if (!currentUser) {
                      setSignUpVisible(true)
                      return
                    }
                    followingMutation.mutate(!isFollowedByMe)
                    setIsFollowedByMe((v: boolean) => !v)
                  }}
                  skeleton
                >
                  Follow
                </Button>
              )
              : (
                <Button
                  className={styles.btnFollow}
                  onClick={() => {
                    if (window.innerWidth < 768) {
                      setIsGetAppModal(Boolean(window.innerWidth < 768))
                      return
                    }
                    followingMutation.mutate(!isFollowedByMe)
                    setIsFollowedByMe((v: boolean) => !v)
                  }}
                  skeleton
                >
                  Following
                </Button>
              )
          }
          </div>

          <div
            ref={descriptionRef}
            className={clsx(styles.infoText, !showFullDescription && styles.infoTextShort)}
          >
            {assetData?.description}
          </div>

          {showViewMore && (
          <Button
            className={styles.infoMore}
            onClick={() => setShowFullDescription((v: boolean) => !v)}
            link
          >
            {showFullDescription ? 'View less' : 'View more'}
          </Button>
          )}

          <div className={styles.infoParams}>
            {Object.keys(assetData?.desc_details || {}).map((item: string) => (
              <div key={item} className={styles.infoParamsItem}>
                <div className={styles.infoParamsTitle}>
                  {item}
                </div>
                <div className={styles.infoParamsValue}>
                  {formatDescription(assetData?.desc_details?.[item])}
                </div>
              </div>
            ))}
          </div>

          {
              assetData?.category?.is_active_addtitional_informations !== false && (
              <>
                <div className={styles.statTitle}>
                  Stats
                </div>

                <div className={styles.stat}>
                  {Object.keys(statsFinance || {}).map((item: string) => {
                  // @ts-ignore
                  // eslint-disable-next-line no-unsafe-optional-chaining
                    if (!statsFinance?.[item] || +statsFinance?.[item] === 0) {
                      return ''
                    }
                    return (
                      <div key={`stat-fin-${item}`} className={styles.statItem}>
                        <div className={styles.statItemTitle}>
                          {item}
                        </div>
                        <div className={styles.statItemValue}>
                          {getItemSuffix(item, statsFinance?.[item])}
                          {formatAssetStat(statsFinance?.[item], true)}
                          {getItemPostffix(item)}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </>
              )
          }

        </div>
      </div>
      <div className={styles.exploreMore}>
        <Button
          className={styles.btn}
          wide
          onClick={() => {
            if (window.innerWidth < 768) {
              setIsGetAppModal(Boolean(window.innerWidth < 768))
              return
            }
            if (onSaveCurrentState) {
              onSaveCurrentState()
            }
            navigate(`/asset/${assetData?.category?.name}/${assetData?.ticker}`)
          }}
        >
          Explore more
        </Button>
      </div>
    </div>
  )
}
export default AssetDetails
